import * as React from 'react';
import { resolve } from 'inversify-react';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';

import { ToastNotifications } from 'src/core/ui/toast';
import { ContextService } from 'src/core/services/context.service';
import { ControllerProps } from 'src/router';
import { ObjectUtils } from 'src/core/utils/object';
import { ProductCategoryFormView } from './product-category-form.view';
import { ProductCategoryDataService } from './product-category-form.data';
import { ConfigurationService } from 'src/core/services/configuration.service';

interface Props extends ControllerProps<{ id: string; }> {
}

interface State {
	mode: ControlMode;
	defaultLanguage: string;
}

@observer
export class ProductCategoryFormController extends React.Component<Props, State> {
	@resolve
	private contextService: ContextService;

	@resolve
	private toast: ToastNotifications;

	@resolve
	private configurationService: ConfigurationService;

	@observable
	private dataService: ProductCategoryDataService;

	constructor(props: Readonly<Props>) {
		super(props);

		this.state = {
			mode: 'display',
			defaultLanguage: ''
		};
	}

	componentDidMount(): void {
		this.props.setBusy(true);
		this.loadData()
			.catch((error) => {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			})
			.then(() => {
				this.props.setBusy(false);
			});
	}

	private async loadData(): Promise<void> {
		this.dataService = ProductCategoryDataService.make(this.contextService);
		const appConfig = await this.configurationService.current();

		this.setState({
			defaultLanguage: appConfig.defaultLanguage
		});

		const id = this.props.match.params.id;
		if (id === 'new') {
			// Create new
			const item = await this.dataService.add(appConfig.defaultLanguage);
			this.handleModeChange('edit');
		} else {
			// Load entity
			const productCategoryId = parseInt(id);
			this.dataService.addFilter('id', 'eq', productCategoryId);
			this.dataService.addSorting('id', 'asc', true);
			this.dataService.pageSize = 1;
			await this.dataService.load(query => query);
		}
	}

	@computed
	protected get title(): string {
		const { defaultLanguage } = this.state;
		if (this.dataService && this.dataService.item && this.dataService.item.dynamicProperties.displayName) {
			return `Category ${this.dataService.item.dynamicProperties['displayName'][defaultLanguage]}`;
		}

		return '';
	}

	handleModeChange = (mode: ControlMode): void => {
		this.setState({ mode });
	};

	save = async (): Promise<void> => {
		const item = this.dataService.item;
		const errors = await item.validate(true);
		if (errors.length === 0) {
			try {
				this.props.setBusy(true);
				item.services = undefined;
				await this.dataService.save();
				this.handleModeChange('display');
				this.toast.showDataSaved();
				this.props.history.replace(`/products/categories`);
			} catch (error) {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			} finally {
				this.props.setBusy(false);
			}
		} else {
			this.toast.showToast('Validation error!', errors.map(o => o.text), 'error');
		}
	};

	cancel = (): void => {
		this.props.history.push('/products/categories');
	};

	render(): React.ReactNode {
		const { defaultLanguage, mode } = this.state;

		if (this.dataService == null || this.dataService.item == null) {
			return null;
		}

		return (
			<ProductCategoryFormView
				title={this.title}
				defaultLanguage={defaultLanguage}
				mode={mode}
				contextService={this.contextService}
				onModeChange={this.handleModeChange}
				cancel={this.cancel}
				save={this.save}
				model={this.dataService.item}
				setBusy={this.props.setBusy}
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/products/product-categories/form/product-category-form.controller.tsx